import { Injectable } from '@angular/core';
import { BusinessComponent, BusinessComponentOptions, ReportSection } from '@blk/simba';
import { ViewCategory } from '@ddt-web/config';
import { ClientOutput, OutputFlag } from '@ddt-web/domain';
import { SectionDisclaimerComponent, SectionTitleComponent, SectionTitleOptions, StaticContentSection } from '@ddt-web/pdf-components';
import { AchoReportConfiguration, ReportUtils } from '@ddt-web/pdf-utils';

import { PdfReportSectionProperties } from '../models/pdf-report-config.interface';

@Injectable({
	providedIn: 'root'
})
export abstract class ReportConfigService {
	protected disclaimerTextClasses: string[];
	public selectedModelIds: string[];

	public getReport(reportType: string, outputFlags?: OutputFlag[], customizationMap: Map<string, PdfReportSectionProperties> = new Map()): AchoReportConfiguration {
		switch (reportType) {
			case ViewCategory.FA_SELF_SERVICE_PDF:
				return this.getSelfServiceConfig(outputFlags, customizationMap, this.selectedModelIds);
			case ViewCategory.CLIENT_PDF:
				return this.getClientConfig(outputFlags, customizationMap);
			case ViewCategory.PROPOSAL_PDF:
				return this.getProposalConfig(outputFlags, customizationMap);
			case ViewCategory.FA_PLUS_PDF:
				return this.getPortfolioSolutionsConfig(outputFlags, customizationMap);
			default:
				return this.getClientConfig(outputFlags, customizationMap);
		}
	}

	protected getReportCoverPage(reportType, outputFlags: OutputFlag[]): string {
		const reportCoverPage = this.getReportPage(reportType, outputFlags, 0) as StaticContentSection;
		const reportCoverPageSvg = reportCoverPage.getResult();
		return ReportUtils.parseSvgElement(reportCoverPageSvg);
	}

	protected getReportPage(reportType, outputFlags: OutputFlag[], pageIndex: number): ReportSection {
		const reportConfig = this.getReport(reportType, outputFlags);
		const reportPageElement = reportConfig.pages[pageIndex];
		return reportPageElement;
	}

	protected getSectionTitleAndRepeatedDisclaimer(
		componentId: string,
		titleDcrKey: string,
		title: string,
		disclaimerDcrKey: string,
		disclaimerDefaultText: string,
		showPoweredBy: boolean = true,
		isDisclaimerRepeated: boolean = true
	): BusinessComponent[] {
		let titleOptions: SectionTitleOptions = null;
		if (showPoweredBy) {
			titleOptions = { subTextDcr: 'POWERED_BY_ALADDIN', subTextDefaultText: 'Powered by Aladdin' };
		}
		return [
			new SectionTitleComponent(
				titleDcrKey, title,
				new BusinessComponentOptions(componentId, true, { isRepeatComponent: false, skipPages: [0, 1] }), false, titleOptions
			),
			new SectionDisclaimerComponent(
				disclaimerDcrKey, disclaimerDefaultText,
				new BusinessComponentOptions(componentId, true, { isRepeatComponent: isDisclaimerRepeated, skipPages: [0, 1] }), false, ['text-10']
			)
		];
	}

	/** DO NOT USE THESE METHODS DIRECTLY */
	public abstract getReportCover(reportType: string, clientOutput: ClientOutput): string;
	public abstract getSelfServiceConfig(
		outputFlags: OutputFlag[],
		customizationMap?: Map<string, PdfReportSectionProperties>,
		selectedIds?: string[],
		familyIdentifier?: string,
		groupIdentifier?: string,
		excludePerformance?: boolean
	): AchoReportConfiguration;
	public abstract getClientConfig(outputFlags: OutputFlag[], customizationMap?: Map<string, PdfReportSectionProperties>): AchoReportConfiguration;
	public abstract getProposalConfig(outputFlags: OutputFlag[], customizationMap?: Map<string, PdfReportSectionProperties>): AchoReportConfiguration;
	public abstract getPortfolioSolutionsConfig(outputFlags: OutputFlag[], customizationMap?: Map<string, PdfReportSectionProperties>): AchoReportConfiguration;
}
